import { createContext, type ReactNode, useContext } from "react";
import { cn } from "@/lib/cn";

// When the shell's top bar already shows the page title the header must not repeat it:
// always in the employee shell (the top bar is there at every width), under `md` in the
// dispatcher shell (the sidebar takes over from the top bar). Set by AppShell.
export type TitleInTopBar = "always" | "mobile" | "never";
export const TitleInTopBarContext = createContext<TitleInTopBar>("never");

export function PageHeader({
  title,
  subtitle,
  actions,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  const titleInTopBar = useContext(TitleInTopBarContext);
  const hasMore = Boolean(subtitle || actions);
  // Nothing besides the title, and the top bar shows it: render nothing at all.
  if (titleInTopBar === "always" && !hasMore) return null;
  return (
    <div
      className={cn(
        "mb-4 flex flex-wrap items-start justify-between gap-3",
        titleInTopBar === "mobile" && !hasMore && "max-md:hidden",
        className,
      )}
    >
      <div>
        <h1
          className={cn(
            "font-semibold text-lg text-zinc-900 md:text-xl",
            titleInTopBar === "always" && "hidden",
            titleInTopBar === "mobile" && "max-md:hidden",
          )}
        >
          {title}
        </h1>
        {subtitle ? <p className="text-sm text-zinc-500">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}
